import { useTranslation } from "react-i18next";
import { Modal } from "../../../../components/modal/Modal";
import { Flex } from "../../../../components/Flex";
import { Button } from "../../../../components/Button";
import { Text } from "../../../../components/Text";
import styled from "styled-components";
import { Spacing } from "../../../../theme/Spacing";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons/faArrowRight";
import { IntegrationConfigWhatsappDirectChat } from "../../../../model/integration/types/config/IntegrationConfigWhatsappDirectChat";
import { Integration } from "../../../../model/integration/Integration";

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  color: ${({ theme }) => theme.main};
  background: ${({ theme }) => theme.white};
  border: 1px solid ${({ theme }) => theme.main};
`;

type Props = {
  open: boolean;
  onClose: () => void;
  qrImage: string | null;
  integrationId: string;
};

export const IntegrationWhatsAppDirectQRModal: React.FC<Props> = ({
  open,
  onClose,
  qrImage,
  integrationId,
}) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const onConfirmScan = async () => {
    try {
      setLoading(true);
      await dispatch(
        integrationActions.syncWhatsappDirectStatus({
          userId,
          integrationId,
        }),
      ).unwrap();

      const integrations = await dispatch(
        integrationActions.getUserIntegrations({
          userId,
        }),
      ).unwrap();
      const updatedIntegration:
        | Integration<IntegrationConfigWhatsappDirectChat>
        | undefined = integrations.find(
        (integration) => integration._id === integrationId,
      );

      if (
        updatedIntegration &&
        updatedIntegration.config.clientStatus === "authorized"
      ) {
        AtiraToast.success(
          t("integration.add-modal.whatsapp-direct.qr.modal.success"),
        );
      }
      onClose();
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      mask={{ closable: false }}
      title={t("integration.add-modal.whatsapp-direct.qr.modal.title")}
    >
      <Flex flexDirection="column" alignItems="center" gap="m">
        <Text>{t("integration.add-modal.whatsapp-direct.qr.tooltip")}</Text>
        <img
          src={qrImage || ""}
          alt={t("integration.add-modal.whatsapp-direct.qr.modal.title")}
          style={{ width: "100%", maxWidth: "300px" }}
        />
        <Text fontSize="s" color="darkerSub" align="center">
          {t("integration.add-modal.whatsapp-direct.qr.modal.description")}
        </Text>

        <Flex gap="m" justifyContent="center" width="100%">
          <StyledButton onClick={onClose}>{t("common.cancel")}</StyledButton>

          <Button onClick={onConfirmScan} loading={loading} icon={faArrowRight}>
            {t("integration.add-modal.whatsapp-direct.qr.modal.button")}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
